import { Injectable, inject } from '@angular/core';
import { AppConfig } from 'client/app/app.config';
import { ApiClientConstant } from '@cureskin/api-client';
import { AiParameterInput } from '../../helper/report-helper/report-helper';
import {
  AiDetectionResult,
  CROWN_DENSITY_TARGET_RANGE,
  PARTITION_WIDTH_TARGET_RANGE,
  SCALP_SEGMENT_CLASS,
  SCALP_SEVERITY_MULTIPLIERS,
  SCALP_VISIBILITY_TARGET_RANGE,
  ScalpCardCopy,
  ScalpSeverityMultipliers,
  ScalpCardState,
  ScalpSeverity,
  ScalpSeverityLanguageCopy,
  ScalpSeverityValue,
  HairFrontCardState,
  HairDetectionRoute,
} from './concern-detections.constants';
import { scalpSeverityCopy } from '../../static-translations/scalp-severity-copy';

const SCALP_LABEL_SEVERITIES: string[] = [ScalpSeverity.Mild, ScalpSeverity.Moderate, ScalpSeverity.Severe];

@Injectable({
  providedIn: 'root',
})
export class ConcernDetectionsService {
  private readonly appConfig: AppConfig = inject(AppConfig);

  resolveHairDetectionRoute(params: {
    type?: string,
    aiOriginalType?: string,
    results: AiDetectionResult[],
    experimentEnabled?: boolean,
  }): HairDetectionRoute {
    const routeType = params.aiOriginalType ?? params.type;
    const isHairTopType = routeType === ApiClientConstant.InstantCheckup.Type.HAIR_TOP;
    const isHairFrontType = routeType === ApiClientConstant.InstantCheckup.Type.HAIR_FRONT;
    const hasScalpDetections = params.results.some((r: AiDetectionResult): boolean =>
      r.class === SCALP_SEGMENT_CLASS);
    return {
      isHairTopType,
      isHairFrontType,
      showNoScalpDetectionCard: hasScalpDetections,
    };
  }

  getHairFrontCardState(params: {
    isFilledHairFrontFlow: boolean;
    isHairFrontType: boolean;
    isTempleBaldnessDetected: boolean;
    isHairlineDetected: boolean;
  }): HairFrontCardState {
    if (!params.isFilledHairFrontFlow || !params.isHairFrontType) return HairFrontCardState.None;
    if (params.isTempleBaldnessDetected) return HairFrontCardState.TempleBaldness;
    if (params.isHairlineDetected) return HairFrontCardState.HairlineMapped;
    return HairFrontCardState.Captured;
  }

  getScalpSegmentDetections(results: AiDetectionResult[]): AiDetectionResult[] {
    return (results || []).filter((result: AiDetectionResult): boolean =>
      result.Condition === this.appConfig.Shared.InstantCheckup.DetectionStatus.DETECTED
      && result.class === SCALP_SEGMENT_CLASS);
  }

  getNormalizedScalpSeverity(detection?: AiDetectionResult): string | undefined {
    const rawSeverity = detection?.severity;
    return rawSeverity
      ? rawSeverity.charAt(0).toUpperCase() + rawSeverity.slice(1).toLowerCase()
      : undefined;
  }

  getNormalScalpDetection(results: AiDetectionResult[]): AiDetectionResult | undefined {
    return (results || []).find((result: AiDetectionResult): boolean =>
      result.class === SCALP_SEGMENT_CLASS && this.getNormalizedScalpSeverity(result) === ScalpSeverity.Normal);
  }

  hasDrawableScalpMasks(results: AiDetectionResult[]): boolean {
    return this.getScalpSegmentDetections(results).some((detection: AiDetectionResult): boolean => {
      try {
        if (!detection?.scalp_masks) return false;
        const scalpMasks = JSON.parse(detection.scalp_masks || '[]');
        return Array.isArray(scalpMasks) && scalpMasks.length > 0;
      } catch (error) {
        return false;
      }
    });
  }

  getScalpCardState(params: {
    isHairTopView: boolean,
    results: AiDetectionResult[],
    showNoDetectionCard?: boolean,
    hairImageType?: string,
  }): ScalpCardState {
    if (!params.isHairTopView) return ScalpCardState.None;
    if (this.getNormalScalpDetection(params.results)) return ScalpCardState.Normal;
    if (this.hasDrawableScalpMasks(params.results)) return ScalpCardState.Detected;
    if (params.hairImageType === 'FRONT') return ScalpCardState.HairFrontDetected;
    const hasScalpSegment = params.results.some((r: AiDetectionResult): boolean =>
      r.class === SCALP_SEGMENT_CLASS);
    return hasScalpSegment ? ScalpCardState.NoDetection : ScalpCardState.None;
  }

  isScalpLabelSeverity(severity?: string): boolean {
    return !!severity && SCALP_LABEL_SEVERITIES.includes(severity);
  }

  getScalpCardCopy(params: {
    state: ScalpCardState,
    severity?: string,
    gender?: string,
    language?: string,
  }): ScalpCardCopy | undefined {
    const copy: ScalpSeverityLanguageCopy = scalpSeverityCopy[params.language] || scalpSeverityCopy.en;
    switch (params.state) {
      case ScalpCardState.NoDetection:
        return {
          title: copy.noDetection.title,
          reasonsHeading: copy.noDetection.reasonsHeading,
          reasons: copy.noDetection.reasons,
        };
      case ScalpCardState.HairFrontDetected:
        return {
          title: copy.hairFrontDetected.title,
          description: copy.hairFrontDetected.description,
        };
      case ScalpCardState.Normal:
        return {
          title: copy.titles[ScalpSeverity.Normal],
          description: this.getScalpDescriptions(copy, params.gender)[ScalpSeverity.Normal],
        };
      case ScalpCardState.Detected: {
        const severity = this.toCopySeverity(params.severity);
        return {
          title: copy.titles[severity],
          description: this.getScalpDescriptions(copy, params.gender)[severity],
        };
      }
      default:
        return undefined;
    }
  }

  getScalpParameterInputs(params: { gender?: string, detection?: AiDetectionResult, language?: string }): AiParameterInput[] | null {
    const severityRatio = this.toFiniteNumber(params.detection?.severity_ratio);
    const scalpAreaRatio = this.toFiniteNumber(params.detection?.scalp_area_ratio);
    if (!severityRatio || severityRatio < 0 || !scalpAreaRatio || scalpAreaRatio < 0) return null;

    const multipliers = this.getScalpSeverityMultipliers(params.detection);
    const labels = (scalpSeverityCopy[params.language] || scalpSeverityCopy.en).parameterLabels;
    const scalpVisibility: AiParameterInput = {
      name: labels.scalpVisibility,
      valuePercent: this.inflateRatioPercent(scalpAreaRatio * 100, multipliers.ratioInflation),
      targetRange: SCALP_VISIBILITY_TARGET_RANGE,
    };

    if (params.gender === ApiClientConstant.User.Gender.FEMALE) {
      return [
        {
          name: labels.partitionWidth,
          valuePercent: this.inflateRatioPercent(severityRatio * 100, multipliers.ratioInflation),
          targetRange: PARTITION_WIDTH_TARGET_RANGE,
        },
        scalpVisibility,
      ];
    }

    const hairMaskArea = this.toFiniteNumber(params.detection?.hair_mask_area) ?? 0;
    const scalpMaskArea = this.toFiniteNumber(params.detection?.scalp_mask_area) ?? 0;
    const totalMaskArea = hairMaskArea + scalpMaskArea;
    if (totalMaskArea <= 0) return null;

    return [
      {
        name: labels.crownDensity,
        valuePercent: multipliers.crownDensityCoefficient * 100 * (hairMaskArea / totalMaskArea),
        targetRange: CROWN_DENSITY_TARGET_RANGE,
      },
      scalpVisibility,
    ];
  }

  private getScalpSeverityMultipliers(detection?: AiDetectionResult): ScalpSeverityMultipliers {
    const severity = this.getNormalizedScalpSeverity(detection) as ScalpSeverityValue;
    return SCALP_SEVERITY_MULTIPLIERS[severity] ?? SCALP_SEVERITY_MULTIPLIERS[ScalpSeverity.Unknown];
  }

  private inflateRatioPercent(rawPercent: number, inflation: number): number {
    const inflated = rawPercent * inflation;
    return inflated > 100 ? rawPercent : inflated;
  }

  private toFiniteNumber(value?: number | string): number | undefined {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private getScalpDescriptions(
    copy: ScalpSeverityLanguageCopy,
    gender?: string,
  ): Record<ScalpSeverityValue, string> {
    return gender === ApiClientConstant.User.Gender.FEMALE
      ? copy.descriptions.Female
      : copy.descriptions.Male;
  }

  private toCopySeverity(severity?: string): ScalpSeverityValue {
    return this.isScalpLabelSeverity(severity)
      ? severity as ScalpSeverityValue
      : ScalpSeverity.Unknown;
  }
}
